import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './Process.module.css';

const steps = [
  {
    num: '01',
    title: 'Discovery & Vision',
    desc: 'We begin with a conversation — your space, your story, your aesthetic. I listen carefully to understand the feeling you want to create and the message you want your art to carry.',
  },
  {
    num: '02',
    title: 'Concept & Design',
    desc: "I develop initial sketches, mood boards, and colour palettes tailored to your project. You'll receive clear concepts for review, and we refine until the vision feels exactly right.",
  },
  {
    num: '03',
    title: 'Creation',
    desc: 'With your approval, I bring the artwork to life. Whether on canvas, wall, or surface, I work with precision and care, keeping you updated throughout the process.',
  },
  {
    num: '04',
    title: 'Delivery & Installation',
    desc: 'The finished piece is delivered or installed with full attention to quality and presentation. Every project ends with your complete satisfaction — that is the only standard.',
  },
];

export default function Process() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className={styles.process} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9 }}
        >
          <span className={styles.sectionLabel}>
            <span className={styles.labelLine} /> How It Works
          </span>
          <h2 className={styles.heading}>
            From <em>Idea</em> to Installation
          </h2>
        </motion.div>

        <div className={styles.steps}>
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              className={styles.step}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 + i * 0.15 }}
            >
              <div className={styles.stepLeft}>
                <span className={styles.stepNum}>{step.num}</span>
                {i < steps.length - 1 && <div className={styles.stepLine} />}
              </div>
              <div className={styles.stepContent}>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDesc}>{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className={styles.callout}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.7 }}
        >
          <div className={styles.calloutText}>
            <h3>Ready to start your project?</h3>
            <p>Let's create something that speaks — on your walls, your canvas, or your fabric.</p>
          </div>
          <a
            href="https://bookanartist.co/artist/artist-for-hire/13184"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.calloutBtn}
          >
            Start a Conversation
          </a>
        </motion.div>
      </div>
    </section>
  );
}
